import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description?: string;
  /** Absolute URL or path. Falls back to current location. */
  canonical?: string;
  /** Open Graph image (absolute URL). Falls back to a brand default. */
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile';
  /** Inline JSON-LD structured data — pass an object, we'll stringify it. */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  /** Tell crawlers not to index this page (e.g. token-based pages). */
  noindex?: boolean;
}

const APP_NAME = (import.meta.env.VITE_APP_NAME as string) ?? 'Locum Network';
const APP_URL = (import.meta.env.VITE_APP_URL as string) ?? (typeof window !== 'undefined' ? window.location.origin : 'https://locumnetwork.com.au');
const DEFAULT_OG = `${APP_URL}/og-default.png`;

/**
 * Mounts page-specific <title>, <meta>, <link rel="canonical"> and
 * optional JSON-LD into the document <head> on render. Cleans up the
 * JSON-LD script when the page unmounts so we don't accumulate stale
 * structured data across navigation.
 *
 * Why a hook-based component instead of react-helmet:
 *   • zero new dependencies
 *   • SSR-friendly when we ship that later (no-op on server)
 *   • each lazy-loaded route can ship its SEO inline
 */
export function SEO({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG,
  ogType = 'website',
  jsonLd,
  noindex,
}: SEOProps) {
  useEffect(() => {
    // <title>
    const fullTitle = title.includes(APP_NAME) ? title : `${title} · ${APP_NAME}`;
    const previousTitle = document.title;
    document.title = fullTitle;

    // <meta>
    const tags: Array<{ name?: string; property?: string; content: string }> = [];
    if (description) {
      tags.push({ name: 'description', content: description });
      tags.push({ property: 'og:description', content: description });
      tags.push({ name: 'twitter:description', content: description });
    }
    tags.push({ property: 'og:title', content: fullTitle });
    tags.push({ property: 'og:type', content: ogType });
    tags.push({ property: 'og:site_name', content: APP_NAME });
    tags.push({ property: 'og:image', content: ogImage });
    tags.push({ name: 'twitter:card', content: 'summary_large_image' });
    tags.push({ name: 'twitter:title', content: fullTitle });
    tags.push({ name: 'twitter:image', content: ogImage });
    if (noindex) tags.push({ name: 'robots', content: 'noindex,nofollow' });

    const created: HTMLElement[] = [];
    for (const t of tags) {
      const attr = t.name ? 'name' : 'property';
      const key = t.name ?? t.property!;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      const wasOurs = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', t.content);
      if (wasOurs) el.dataset.seoOwner = 'route';
    }

    // <link rel="canonical">
    const href = canonical
      ? (canonical.startsWith('http') ? canonical : `${APP_URL}${canonical}`)
      : (typeof window !== 'undefined' ? window.location.href : '');
    let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const linkWasOurs = !link;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);

    // JSON-LD
    let jsonLdEl: HTMLScriptElement | null = null;
    if (jsonLd) {
      jsonLdEl = document.createElement('script');
      jsonLdEl.type = 'application/ld+json';
      jsonLdEl.dataset.seoOwner = 'route';
      jsonLdEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      // Restore title
      document.title = previousTitle;
      // Remove tags we created (leave any pre-existing ones alone)
      for (const el of created) el.remove();
      if (linkWasOurs && link) link.remove();
      if (jsonLdEl) jsonLdEl.remove();
    };
  }, [title, description, canonical, ogImage, ogType, jsonLd, noindex]);

  return null;
}

/** Helper: build a JobPosting schema (https://schema.org/JobPosting). */
export function jobPostingSchema(input: {
  id: string;
  title: string;
  description: string;
  hospitalName: string;
  city: string;
  state: string;
  postcode?: string | null;
  rateAmount: number;
  rateType: 'hour' | 'day' | 'week';
  startDate: string;
  endDate?: string | null;
  jobType: 'locum' | 'contract' | 'permanent' | 'fixed_term';
  postedAt: string;
}): Record<string, unknown> {
  const employmentTypeMap = {
    locum: 'TEMPORARY',
    contract: 'CONTRACTOR',
    permanent: 'FULL_TIME',
    fixed_term: 'FULL_TIME',
  } as const;
  const unitMap = { hour: 'HOUR', day: 'DAY', week: 'WEEK' } as const;
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: input.title,
    description: input.description,
    identifier: { '@type': 'PropertyValue', name: 'Locum Network', value: input.id },
    datePosted: input.postedAt,
    validThrough: input.endDate ?? undefined,
    employmentType: employmentTypeMap[input.jobType],
    jobStartDate: input.startDate,
    hiringOrganization: {
      '@type': 'Organization',
      name: input.hospitalName,
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: input.city,
        addressRegion: input.state,
        postalCode: input.postcode ?? undefined,
        addressCountry: 'AU',
      },
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: 'AUD',
      value: {
        '@type': 'QuantitativeValue',
        value: input.rateAmount,
        unitText: unitMap[input.rateType],
      },
    },
    directApply: true,
  };
}
